import { transformCurrencyAmountDto } from '@marketplace-web/currency/currency-data'

import { FilterCatalogModelWithFilterGroups } from '../types/models/filter-catalog'
import { FilterGroupModel } from '../types/models/filter-group'
import {
  DynamicFilterDto,
  DynamicFilterOptionDto,
  SearchTranslationsDto,
  SelectedDynamicFilterDto,
  TranslationDto,
} from '../types/dtos/dynamic-filter'
import {
  DynamicFilterModel,
  DynamicFilterOptionModel,
  SearchTranslationsModel,
  SelectedDynamicFilterModel,
  TranslationModel,
} from '../types/models/dynamic-filter'
import { SelectableListItem } from '../types/models/catalog-item'
import { OptionType } from '../constants/dynamic-filter'

import { modifySelectedNavigationalFilters } from '../utils/catalog-filter'

export const transformDynamicFilterDefaultOptions = (
  options: Array<DynamicFilterOptionModel>,
  optionType: OptionType,
  isHighlighted?: boolean,
  selectedIds?: Array<number>,
): Array<SelectableListItem<DynamicFilterOptionModel>> => {
  const list =
    optionType === OptionType.Default
      ? options.map(option => ({
          id: option.id,
          title: option.title,
          value: option.id,
          data: option,
        }))
      : []

  if (isHighlighted && selectedIds?.length) {
    const selectedItems = list.filter(item => selectedIds.includes(item.id))
    const remainingItems = list.filter(item => !selectedIds.includes(item.id))

    return [...selectedItems, ...remainingItems]
  }

  return list
}

export const transformDynamicFilterNavigationalOptions = (
  options: Array<DynamicFilterOptionModel>,
  optionType: OptionType,
  isHighlighted?: boolean,
  selectedIds?: Array<number>,
): Array<FilterCatalogModelWithFilterGroups<DynamicFilterOptionModel>> => {
  const navigationalGroups =
    optionType === OptionType.Navigational
      ? options.map(({ id, type, title, options: navOptions }) => ({
          code: `${type}_${id}`,
          title,
          matchingGroups: navOptions.map(
            ({ id: matchingId, title: matchingTitle, options: matchingOptions }) => ({
              id: matchingId,
              description: matchingTitle,
              items: matchingOptions.map(matchingOption => ({
                id: matchingOption.id,
                title: matchingOption.title,
                data: matchingOption,
              })),
            }),
          ),
        }))
      : []

  if (isHighlighted && selectedIds?.length) {
    const modifiedNavigationalGroups =
      optionType === OptionType.Navigational
        ? options.map(({ id, type, title, options: navOptions }) => ({
            code: `${type}_${id}`,
            title,
            matchingGroups: modifySelectedNavigationalFilters(
              navOptions.map(
                ({ id: matchingId, title: matchingTitle, options: matchingOptions }) => ({
                  id: matchingId,
                  description: matchingTitle,
                  items: matchingOptions.map(matchingOption => ({
                    id: matchingOption.id,
                    title: matchingOption.title,
                    data: matchingOption,
                  })),
                }),
              ),
              selectedIds,
            ),
          }))
        : []

    return modifiedNavigationalGroups
  }

  return navigationalGroups
}

export const transformDynamicFilterGroupOptions = (
  options: Array<DynamicFilterOptionModel>,
  optionType: OptionType,
  isHighlighted?: boolean,
  selectedIds?: Array<number>,
): Array<FilterGroupModel<DynamicFilterOptionModel>> => {
  const groups =
    optionType === OptionType.Group
      ? options.map(({ id, title, options: groupOptions }) => ({
          id,
          description: title,
          items: groupOptions.map(groupOption => ({
            id: groupOption.id,
            title: groupOption.title,
            data: groupOption,
          })),
        }))
      : []

  if (isHighlighted && selectedIds?.length) {
    return modifySelectedNavigationalFilters(groups, selectedIds)
  }

  return groups
}

export const transformSearchTranslations = (
  searchTranslations?: SearchTranslationsDto,
): SearchTranslationsModel | undefined => {
  if (!searchTranslations) return undefined

  return {
    placeholder: searchTranslations.placeholder,
    noResults: {
      title: searchTranslations.no_results?.title,
      body: searchTranslations.no_results?.body,
    },
  }
}

export const transformTranslations = (
  translations?: TranslationDto,
): TranslationModel | undefined => {
  if (!translations) return undefined

  return {
    noResults: {
      title: translations.no_results?.title,
      body: translations.no_results?.body,
    },
  }
}

export const transformDynamicFilterOptions = (
  dynamicFilterOptions: Array<DynamicFilterOptionDto> | undefined,
): Array<DynamicFilterOptionModel> => {
  if (!dynamicFilterOptions?.length) return []

  return dynamicFilterOptions.map(
    ({
      id,
      title,
      description,
      type,
      prefix,
      items_count,
      items_count_max,
      options,
    }: DynamicFilterOptionDto): DynamicFilterOptionModel => ({
      id,
      title,
      description,
      type,
      prefix,
      itemsCount: items_count,
      itemsCountMax: items_count_max,
      options: transformDynamicFilterOptions(options),
    }),
  )
}

export const transformDynamicFilter = ({
  id,
  title,
  code,
  display_type,
  selection_type,
  is_selection_highlighted,
  search_translations,
  translations,
  is_new_filter,
  is_hidden,
  is_async_facet,
  is_lazy,
  total_count_max,
  experiments,
  links,
  options,
  minimum_validation,
}: DynamicFilterDto): DynamicFilterModel => ({
  id,
  title,
  type: code,
  displayType: display_type,
  selectionType: selection_type,
  isSelectionHighlighted: is_selection_highlighted,
  searchTranslations: transformSearchTranslations(search_translations),
  translations: transformTranslations(translations),
  isNewFilter: is_new_filter,
  isHidden: is_hidden,
  isAsyncFacet: is_async_facet,
  isLazy: is_lazy,
  totalCountMax: total_count_max,
  experiments,
  links,
  minimumValidation: minimum_validation && transformCurrencyAmountDto(minimum_validation),
  options: transformDynamicFilterOptions(options),
})

export const transformDynamicFilters = (
  dynamicFilters: Array<DynamicFilterDto>,
): Array<DynamicFilterModel> => dynamicFilters.map(transformDynamicFilter)

export const transformSelectedDynamicFilter = ({ code, ids }: SelectedDynamicFilterDto) => ({
  type: code,
  ids,
})

export const transformSelectedDynamicFilters = (
  selectedFilters: Array<SelectedDynamicFilterDto>,
): Array<SelectedDynamicFilterModel> => selectedFilters.map(transformSelectedDynamicFilter)
